import { useCallback } from 'react';
import { useConfirmFileUploadedMutation, useGetPresignedPostMutation } from 'store/slices/files/apis/filesApi';
import { uploadFileToS3 } from 'utils/helpers';
import toUploadFileName from 'utils/toUploadFileName';

import type { TPresignedFileTypes } from 'constants/interfaces';
import type { IPresignedData } from 'store/slices/files/interfaces/IPresignedFileResponse';

/**
 * Uploads a file through `/files/get-pre-signed-post` and resolves to the pre-signed data.
 * Store `fields.Url` as the file reference. Rejects only when the pre-signed request fails.
 */
const useFileStorageUpload = () => {
  const [getPresignedPost, { isLoading }] = useGetPresignedPostMutation();
  const [confirmFileUploaded] = useConfirmFileUploadedMutation();

  const uploadToStorage = useCallback((file: File, fileType: TPresignedFileTypes): Promise<IPresignedData> => (
    getPresignedPost({ fileType, originalName: toUploadFileName(file.name) })
      .unwrap()
      .then(({ data }) => uploadFileToS3(file, data)
        .then((isUploaded) => {
          // Not awaited and errors ignored: the backend also polls S3 and marks the file uploaded.
          if (isUploaded) {
            confirmFileUploaded({ id: data.file.id });
          }
          return data;
        }))
  ), [getPresignedPost, confirmFileUploaded]);

  return { uploadToStorage, isLoading };
};

export default useFileStorageUpload;
